import type {
  ListaItens,
  SeletorItem,
} from '@Lar21/imaginii-web-components/vue'

import type {
  FiltrosCatalogoEmpregador,
  OpcaoEmpregador,
  SelecaoCatalogoEmpregador,
} from '../catalogo-empregador.tipos'

export const CHAVE_I18N_CATALOGO_EMPREGADOR =
  'srcNext.estruturaDePontoDaEmpresa.catalogoEmpregador'

type Traduzir = (chave: string) => string

export const criarItemSeletorEmpregador = (
  opcao: OpcaoEmpregador,
): SeletorItem => ({
  chave: opcao.id,
  valor: opcao.name,
  ...(typeof opcao.code === 'string' && opcao.code.trim() ? { legenda: opcao.code } : {}),
})

const normalizarOpcoesSelecionadas = (
  valor: SelecaoCatalogoEmpregador,
  selecaoMultipla: boolean,
): readonly OpcaoEmpregador[] => {
  if (valor === null) return []
  if (!Array.isArray(valor)) return [valor as OpcaoEmpregador]
  if (selecaoMultipla) return valor as readonly OpcaoEmpregador[]
  return valor[0] ? [valor[0] as OpcaoEmpregador] : []
}

export const filtrarOpcoesEmpregador = (
  opcoes: readonly OpcaoEmpregador[],
  filtros?: FiltrosCatalogoEmpregador,
  predicado?: (empregador: OpcaoEmpregador) => boolean,
): readonly OpcaoEmpregador[] => {
  if ((!filtros || Object.keys(filtros).length === 0) && !predicado) return opcoes
  return opcoes.filter(opcao => (
    (filtros?.id === undefined || (typeof opcao.id === 'string' && filtros.id.includes(opcao.id)))
    && (filtros?.visivel === undefined || opcao.visivel === filtros.visivel)
    && (filtros?.jurisdicao === undefined || (typeof opcao.jurisdicao === 'string' && filtros.jurisdicao.includes(opcao.jurisdicao)))
    && (predicado === undefined || predicado(opcao))
  ))
}

export const criarEstadoSeletorCatalogoEmpregador = (
  opcoes: readonly OpcaoEmpregador[],
  valor: SelecaoCatalogoEmpregador,
  selecaoMultipla: boolean,
): ListaItens => {
  const carregados = opcoes.filter(opcao => typeof opcao.id === 'string' && opcao.id.trim()
    && typeof opcao.name === 'string' && opcao.name.trim()).map(criarItemSeletorEmpregador)
  const itensPorId = new Map(
    carregados.map((item) => [String(item.chave), item]),
  )
  const selecionados = normalizarOpcoesSelecionadas(valor, selecaoMultipla)
    .flatMap((opcao) => {
      const item = itensPorId.get(opcao.id)
      return item ? [item] : []
    })

  return { carregados, selecionados }
}

export const extrairValorDoSeletorCatalogoEmpregador = (
  estado: ListaItens,
  opcoes: readonly OpcaoEmpregador[],
  selecaoMultipla: boolean,
): SelecaoCatalogoEmpregador => {
  const opcoesPorId = new Map(opcoes.map((opcao) => [opcao.id, opcao]))
  const idsSelecionados = [...new Set(
    estado.selecionados.map(({ chave }) => String(chave)),
  )]
  const opcoesSelecionadas = idsSelecionados.flatMap((id) => {
    const opcao = opcoesPorId.get(id)
    return opcao ? [opcao] : []
  })
  return selecaoMultipla
    ? opcoesSelecionadas
    : (opcoesSelecionadas[0] ?? null)
}

export const criarPropriedadesPontotelSeletorEmpregador = ({
  traduzir,
  label,
  placeholder,
  erro,
}: Readonly<{
  traduzir: Traduzir
  label?: string
  placeholder?: string
  erro: string | null
}>) => ({
  tamanho: 'M' as const,
  largura: '100%',
  tamanhoPagina: 20,
  pesquisavel: true,
  recarregarTodos: true,
  label: label ?? traduzir(`${CHAVE_I18N_CATALOGO_EMPREGADOR}.label`),
  placeholder: placeholder
    ?? traduzir(`${CHAVE_I18N_CATALOGO_EMPREGADOR}.placeholder`),
  erro,
  traducaoNenhumResultado: traduzir(
    `${CHAVE_I18N_CATALOGO_EMPREGADOR}.nenhumResultado`,
  ),
  traducaoSelecionado: traduzir(
    `${CHAVE_I18N_CATALOGO_EMPREGADOR}.selecionado`,
  ),
  traducaoSelecionadosPlural: traduzir(
    `${CHAVE_I18N_CATALOGO_EMPREGADOR}.selecionados`,
  ),
  traducaoRecarregarTodos: traduzir(
    `${CHAVE_I18N_CATALOGO_EMPREGADOR}.recarregar`,
  ),
  traducaoRecarregado: traduzir(
    `${CHAVE_I18N_CATALOGO_EMPREGADOR}.recarregado`,
  ),
})
